/**
 * UserProfilePage — Full user profile with:
 *   - Fixed-height hero banner (420px, ~2.67:1 aspect, editable)
 *   - Profile avatar + arc overlaid bottom-left of hero (Enabler blue)
 *   - Name / handle / location overlaid on hero with gradient scrim
 *   - Sticky tab bar below hero with Enabler blue left accent + rounded bottom corners
 *   - Mini sticky header (small avatar + name) appears when scrolled past hero
 *   - ConfigurableTabSystem: About, Knowledge, Blog, Photos, Feed
 *     (DEV-1455 — the Impact tab moved out to the standalone My Impact page)
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────┐
 *   │  Hero banner (fixed height, gradient scrim)      │
 *   │                                                  │
 *   │  ┌──────┐  Name / @handle / Location   [btns]  │
 *   │  │avatar│  ← arc decoration + flags             │
 *   ├──┴──────┴───────────────────────────────────────┤
 *   │  │ [mini header if scrolled]                     │
 *   │  │ [ About | Knowledge | … ]  (sticky)          │
 *   ├─────────────────────────────────────────────────┤
 *   │  Tab content                                     │
 *   └─────────────────────────────────────────────────┘
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { i18n } from '../../i18n';
import {
  fetchUserByGuid,
  updateUserProfile,
  fetchAllCommunityMemberships,
  checkScreenNameAvailable,
  type SecurityUserResponse,
  type UpdateUserProfileRequest,
  type CommunityMembershipResponse,
} from '../../api/security';
import {
  fetchAllCommunityEntitiesAdmin,
  fetchAllRelationships,
  communityEntityToSeedShape,
  relationshipToSeedShape,
} from '../../api/communityMetadata';
import { pathFromLeafGuid, pathDisplay } from '../../utils/entityPath';
import { uploadDataUrl, fileUrl } from '../../api/fileStorage';
import { mediaSrc } from '../../api/mediaUrl';
import { clearNumberFormatCache } from '../../utils/numberFormat';
import { useTheme, useIsLightTheme } from '../../theme';
import { useUserThemeApi } from '../../api/userTheme';
import CircleCropModal from '../../Components/CircleCropModal/CircleCropModal';
import ConfigurableTabSystem from '../../Components/Tabs/ConfigurableTabSystem';
import FollowButton from '../../Components/FollowButton/FollowButton';
import CountryFlag from '../../Components/CountryFlag/CountryFlag';
import type { TabDefinition } from '../../lib/pillarTypes';
import { usePresence } from '../../presence/usePresence';
import ProgressGallery from '../../Components/ProgressGallery/ProgressGallery';
// MyWorkspacesTab removed — workspaces now accessed via /problems under Lead nav
import KnowledgeTab from './KnowledgeTab';
import BlogTab from './BlogTab';
import ProfileSkillsSection from './ProfileSkillsSection';
// Binary avatar ring (Skip 2026-07-08, DEV-1437) — the hero swoosh keys off
// the viewed user's rung BASICS, not archetype or the sysadmin proxy.
import { hasBlueRing } from '../../Data/Onboarding/ringStatus';
import { isNotificationSoundEnabled, setNotificationSoundEnabled } from '../../notifications/notificationSound';
import { useColorBlindMode } from '../../theme/colorBlindMode';
// DEV-945 cleanup: getDefaultProjectView / setDefaultProjectView import
// removed. The module was deleted with DEV-945 (Default Project View
// preference retired); leaving the import in place causes Vite to fail
// on module load when this page is opened.
import { communityConfigStore } from '../../Data/store';
import ProfileCompletionStrip from '../../Components/ProfileCompletion/ProfileCompletionStrip';
import ProfileHintArrow from '../../Components/ProfileCompletion/ProfileHintArrow';
import AvatarPickerModal from '../../Components/AvatarPicker/AvatarPickerModal';
import { profileCompletion } from '../../Data/Onboarding/profileCompletion';
import { resolveCommunityAncestors } from '../../Data/ProjectConfig/projectConfigOverrides';
import AdminGrantModal from '../../Components/AdminGrant/AdminGrantModal';
import TotpSetup from '../../Components/TotpSetup/TotpSetup';
import { useAuth } from '../../auth/AuthContext';
import {
  fetchContactStatus,
  sendContactRequest,
  removeContact,
  blockUser,
  unblockUser,
  type ContactStatusResponse,
} from '../../api/chat';

/* ------------------------------------------------------------------ */
/*  Constants                                                           */
/* ------------------------------------------------------------------ */

const HERO_MAX_WIDTH = 1120;       // hero photo max width
const HERO_HEIGHT = 420;           // 1.5× taller (was 280)
const HERO_ASPECT = HERO_MAX_WIDTH / HERO_HEIGHT; // ~2.67:1
const HERO_CROP_OUTPUT_W = 1920;
const JPEG_QUALITY = 0.92;
const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024;
const PROFILE_SIZE = 102;          // Avatar on hero overlay (Option B, 15% smaller than 120)
const PROFILE_SIZE_SM = 39;        // Mini header avatar
/** Swoosh arc extends beyond the avatar by this amount on each side */
const SWOOSH_PAD = 3 + 22 + 3;    // gap(3) + stroke(22) + margin(3) = 28
const PROFILE_FULL = PROFILE_SIZE + SWOOSH_PAD * 2; // full extent including swoosh

/** ISO 3166-1 alpha-2 → flag emoji */
function countryFlag(code: string | null | undefined): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  return String.fromCodePoint(
    ...Array.from(upper).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

/* ------------------------------------------------------------------ */
/*  Country codes list (common)                                        */
/* ------------------------------------------------------------------ */

const COUNTRY_OPTIONS: { code: string; name: string }[] = [
  { code: 'US', name: 'United States' }, { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' }, { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
  { code: 'SE', name: 'Sweden' }, { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' }, { code: 'FI', name: 'Finland' },
  { code: 'NL', name: 'Netherlands' }, { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' }, { code: 'AT', name: 'Austria' },
  { code: 'IT', name: 'Italy' }, { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' }, { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' }, { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' }, { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' }, { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' }, { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' }, { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' }, { code: 'EG', name: 'Egypt' },
  { code: 'AR', name: 'Argentina' }, { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' }, { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' }, { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' }, { code: 'TH', name: 'Thailand' },
  { code: 'ID', name: 'Indonesia' }, { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czech Republic' }, { code: 'RO', name: 'Romania' },
  { code: 'HU', name: 'Hungary' }, { code: 'GR', name: 'Greece' },
  { code: 'TR', name: 'Turkey' }, { code: 'IL', name: 'Israel' },
  { code: 'AE', name: 'UAE' }, { code: 'SA', name: 'Saudi Arabia' },
].sort((a, b) => a.name.localeCompare(b.name));

/* ------------------------------------------------------------------ */
/*  Arc decoration SVG for profile photo                               */
/* ------------------------------------------------------------------ */

function ProfileArc({ size, collapsed, isEnabler = true }: { size: number; collapsed: boolean; isEnabler?: boolean }) {
  // Non-enabler users get a white arc; enablers get pillar blue.
  const arcColor = isEnabler ? '#3b82f6' : '#e2e8f0';
  const r = size / 2;
  const stroke = collapsed ? 9 : 22;
  const gap = 3;
  const arcR = r + gap + stroke / 2;
  const pad = gap + stroke + 3;
  const svgSize = size + pad * 2;
  const cx = svgSize / 2;
  const cy = svgSize / 2;

  // Arc from ~7 o'clock CW to ~5 o'clock (~300° sweep). Gap at bottom (5→7).
  // Screen coords (Y-down, CW from 3 o'clock):
  //   3=0°, 4=30°, 5=60°, 6=90°, 7=120°, 8=150°, 9=180°, 10=210°, 11=240°, 12=270°
  // Convert screen→CSS conic: CSS = screen + 90° (since screen 0°=3 o'clock, CSS 0°=12 o'clock).
  // Arc starts early so the round linecap is hidden by the mask's transparent zone.
  const startDeg = 110;  // rotated 25° CW total (= 200° CSS conic, inside mask transparent zone)
  const endDeg = 85;     // rotated 25° CW total (~5:50)
  const toRad = (d: number) => (d * Math.PI) / 180;

  const x1 = cx + arcR * Math.cos(toRad(startDeg));
  const y1 = cy + arcR * Math.sin(toRad(startDeg));
  const x2 = cx + arcR * Math.cos(toRad(endDeg));
  const y2 = cy + arcR * Math.sin(toRad(endDeg));

  // CSS conic-gradient mask for angular fade-in along the arc.
  // conic-gradient: 0deg = 12 o'clock, CW.
  // Arc starts at 195° CSS. Mask starts at 190° so it covers the arc start.
  // Hold transparent for 20deg (190°→210°), then fade to opaque over 45deg (210°→255°).
  const maskConic = collapsed
    ? 'none'
    : 'conic-gradient(from 195deg, transparent 0deg, transparent 40deg, black 110deg, black 360deg)';

  return (
    <svg
      width={svgSize}
      height={svgSize}
      viewBox={`0 0 ${svgSize} ${svgSize}`}
      style={{
        position: 'absolute',
        top: -pad,
        left: -pad,
        pointerEvents: 'none',
        filter: collapsed ? 'none' : `drop-shadow(0 2px 10px ${isEnabler ? 'rgba(59,130,246,0.35)' : 'rgba(226,232,240,0.35)'})`,
        WebkitMaskImage: maskConic,
        maskImage: maskConic,
      }}
    >
      <path
        d={`M ${x1} ${y1} A ${arcR} ${arcR} 0 1 1 ${x2} ${y2}`}
        fill="none"
        stroke={arcColor}
        strokeWidth={stroke}
        strokeLinecap="butt"
      />
      {/* Round cap only at the visible end (5 o'clock) */}
      <circle
        cx={x2}
        cy={y2}
        r={stroke / 2}
        fill={arcColor}
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  HeroCropOverlay (rectangle crop for banner)                         */
/* ------------------------------------------------------------------ */

function HeroCropOverlay({
  imageSrc,
  naturalW,
  naturalH,
  onConfirm,
  onCancel,
}: {
  imageSrc: string;
  naturalW: number;
  naturalH: number;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(800);
  const [viewportH, setViewportH] = useState(HERO_HEIGHT);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; px: number; py: number } | null>(null);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setViewportW(e.contentRect.width);
        setViewportH(e.contentRect.height);
      }
    });
    ro.observe(el);
    setViewportW(el.clientWidth);
    setViewportH(el.clientHeight || HERO_HEIGHT);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Measure parent aspect ratio so the output canvas matches
  const computedTargetAspect = useMemo(() => {
    const parent = overlayRef.current?.parentElement;
    if (parent && parent.clientWidth > 0 && parent.clientHeight > 0) {
      return parent.clientWidth / parent.clientHeight;
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportW]);

  const baseScale = Math.max(viewportW / naturalW, viewportH / naturalH);
  const displayScale = baseScale * zoom;
  const displayW = naturalW * displayScale;
  const displayH = naturalH * displayScale;

  const clampPan = useCallback(
    (px: number, py: number, z?: number) => {
      const ds = baseScale * (z ?? zoom);
      const dw = naturalW * ds;
      const dh = naturalH * ds;
      const mx = Math.max(0, (dw - viewportW) / 2);
      const my = Math.max(0, (dh - viewportH) / 2);
      return { x: Math.max(-mx, Math.min(mx, px)), y: Math.max(-my, Math.min(my, py)) };
    },
    [baseScale, naturalW, naturalH, viewportW, viewportH, zoom],
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, px: pan.x, py: pan.y };
  }, [pan]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan(clampPan(dragRef.current.px + dx, dragRef.current.py + dy));
  }, [clampPan]);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const next = Math.max(1, Math.min(4, zoom - e.deltaY * 0.002));
    setZoom(next);
    setPan((p) => clampPan(p.x, p.y, next));
  }, [zoom, clampPan]);

  const handleZoomSlider = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setZoom(v);
    setPan((p) => clampPan(p.x, p.y, v));
  }, [clampPan]);

  const handleConfirm = useCallback(() => {
    const targetAspect = computedTargetAspect ?? (viewportW / viewportH);
    const visSrcW = viewportW / displayScale;
    const visSrcH = viewportH / displayScale;
    const visSrcCX = (displayW / 2 - pan.x) / displayScale;
    const visSrcCY = (displayH / 2 - pan.y) / displayScale;

    let srcW: number, srcH: number;
    if (visSrcW / visSrcH > targetAspect) { srcW = visSrcW; srcH = visSrcW / targetAspect; }
    else { srcH = visSrcH; srcW = visSrcH * targetAspect; }

    const srcX = Math.max(0, Math.min(naturalW - srcW, visSrcCX - srcW / 2));
    const srcY = Math.max(0, Math.min(naturalH - srcH, visSrcCY - srcH / 2));

    const outW = HERO_CROP_OUTPUT_W;
    const outH = Math.round(HERO_CROP_OUTPUT_W / targetAspect);
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      onConfirm(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.src = imageSrc;
  }, [imageSrc, viewportW, viewportH, displayScale, displayW, displayH, pan, naturalW, naturalH, onConfirm, computedTargetAspect]);

  const imgStyle: React.CSSProperties = {
    position: 'absolute', width: displayW, height: displayH,
    left: (viewportW - displayW) / 2 + pan.x, top: (viewportH - displayH) / 2 + pan.y,
    userSelect: 'none', pointerEvents: 'none',
  };

  return (
    <div ref={overlayRef} style={{
      position: 'absolute', inset: 0, zIndex: 20,
      background: 'var(--bg-elevated)',
      display: 'flex', flexDirection: 'column',
      borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{
        padding: '8px 12px', fontSize: 13, fontWeight: 600,
        color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>Drag to reposition</span>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{Math.round(zoom * 100)}%</span>
      </div>
      <div
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        style={{
          flex: 1, position: 'relative', overflow: 'hidden',
          cursor: dragRef.current ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <img src={imageSrc} alt="Crop preview" draggable={false} style={imgStyle} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
          {[1, 2].map((i) => (
            <React.Fragment key={i}>
              <div style={{ position: 'absolute', left: `${(i / 3) * 100}%`, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.15)' }} />
              <div style={{ position: 'absolute', top: `${(i / 3) * 100}%`, left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.15)' }} />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{
        padding: '8px 12px', background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>Zoom</span>
        <input
          type="range" min="1" max="4" step="0.01"
          value={zoom} onChange={handleZoomSlider}
          style={{ flex: 1, accentColor: 'var(--accent-primary)', cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={onCancel} style={{
            background: 'var(--bg-hover)', border: '1px solid var(--border-default)',
            borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
            padding: '5px 14px', cursor: 'pointer',
          }}>Cancel</button>
          <button type="button" onClick={handleConfirm} style={{
            background: 'rgba(69,187,71,0.75)', border: '1px solid var(--border-default)',
            borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
            padding: '5px 14px', cursor: 'pointer',
          }}>Save Crop</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Props                                                               */
/* ------------------------------------------------------------------ */

interface UserProfilePageProps {
  currentUserGuid: string;
  /** §7.14/§7.17 — the LIVE Action Center badge count from App's shared
   *  composer, for the finish moment's "Back to Action Center — N tasks
   *  waiting" link. Same source as the nav badge; never a second count. */
  acBadgeCount?: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export default function UserProfilePage({ currentUserGuid, acBadgeCount }: UserProfilePageProps) {
  const { userGuid: paramGuid } = useParams<{ userGuid?: string }>();
  const { t } = useTranslation();
  const targetGuid = paramGuid || currentUserGuid;
  const isOwnProfile = !paramGuid || paramGuid.toUpperCase() === currentUserGuid.toUpperCase();
  const { isOnline } = usePresence(targetGuid);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const viewerIsSystemAdmin = !!authUser?.isSystemAdmin;
  // DEV-1473 — the hero bleed is a dark-theme effect; suppressed in light.
  const isLightTheme = useIsLightTheme();

  // ── Admin Grant modal state ──
  const [showAdminGrant, setShowAdminGrant] = useState(false);

  // DEV-1455: the Impact tab is gone from the profile — the user's impact
  // lives on the My Impact page now. The impact-signature fetch went with it.

  // ── Scroll tracking (for mini sticky header visibility) ──
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [containerW, setContainerW] = useState(HERO_MAX_WIDTH);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = () => setScrolledPastHero(el.scrollTop > HERO_HEIGHT * 0.6);
    el.addEventListener('scroll', handler, { passive: true });
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerW(entry.contentRect.width);
    });
    ro.observe(el);
    return () => { el.removeEventListener('scroll', handler); ro.disconnect(); };
  }, []);

  // Fixed hero height — responsive below max width
  const heroH = containerW < HERO_MAX_WIDTH
    ? containerW / HERO_ASPECT
    : HERO_HEIGHT;

  // ── Contact status (for other users' profiles) ──
  const [contactStatus, setContactStatus] = useState<ContactStatusResponse | null>(null);
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    if (isOwnProfile) return;
    setContactLoading(true);
    fetchContactStatus(targetGuid)
      .then(setContactStatus)
      .catch(() => setContactStatus(null))
      .finally(() => setContactLoading(false));
  }, [isOwnProfile, targetGuid]);

  const handleRequestContact = async () => {
    if (contactLoading) return;
    setContactLoading(true);
    try {
      await sendContactRequest(targetGuid);
      setContactStatus({ status: 'pending', contactId: null, direction: 'outgoing' });
    } catch (err) {
      console.error('[UserProfilePage] Failed to send contact request:', err);
    }
    setContactLoading(false);
  };

  const handleRemoveContact = async () => {
    if (!contactStatus?.contactId || contactLoading) return;
    setContactLoading(true);
    try {
      await removeContact(contactStatus.contactId);
      setContactStatus({ status: 'none', contactId: null, direction: null });
    } catch (err) {
      console.error('[UserProfilePage] Failed to remove contact:', err);
    }
    setContactLoading(false);
  };

  const handleBlockFromProfile = async () => {
    if (contactLoading) return;
    setContactLoading(true);
    try {
      await blockUser(targetGuid);
      setContactStatus({ status: 'blocked_by_me', contactId: null, direction: null });
    } catch (err) {
      console.error('[UserProfilePage] Failed to block user:', err);
    }
    setContactLoading(false);
  };

  const handleUnblockFromProfile = async () => {
    if (contactLoading) return;
    setContactLoading(true);
    try {
      await unblockUser(targetGuid);
      setContactStatus({ status: 'none', contactId: null, direction: null });
    } catch (err) {
      console.error('[UserProfilePage] Failed to unblock user:', err);
    }
    setContactLoading(false);
  };

  // ── API state ──
  const [user, setUser] = useState<SecurityUserResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Form state ──
  const [formName, setFormName] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formPrimaryLocation, setFormPrimaryLocation] = useState('');
  const [formScreenName, setFormScreenName] = useState('');
  const [formCountryCode, setFormCountryCode] = useState('');
  const [formOperationalLocation, setFormOperationalLocation] = useState('');
  const [formShowHome, setFormShowHome] = useState(false);
  const [formShowOp, setFormShowOp] = useState(false);
  const [formIsSearchable, setFormIsSearchable] = useState(false);
  const [screenNameStatus, setScreenNameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  // ── Inline bio editor on the About tab (Skip, 2026-07-07) ──
  // The bio was only editable at the BOTTOM of the Account Settings modal —
  // effectively invisible, yet required by the complete_profile rung basic /
  // Getting-started checklist. The About section now edits it in place.
  const [bioEditing, setBioEditing] = useState(false);
  const [bioDraft, setBioDraft] = useState('');
  const [bioBusy, setBioBusy] = useState(false);

  // Binary avatar ring (Skip ruling 2026-07-08, DEV-1437): the hero swoosh
  // reflects whether the VIEWED user has done the rung BASICS — blue when
  // profile + follows + current EULA + standards are all in place, white
  // otherwise, regardless of appointed archetype. Shared predicate:
  // ringStatus.hasBlueRing (accepts the camelCase SecurityUserResponse).
  const ringIsEnabler = hasBlueRing(user);

  // ── Image state ──
  const [heroUrl, setHeroUrl] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [profileImgValid, setProfileImgValid] = useState(true);

  // ── Crop state ──
  const [heroCrop, setHeroCrop] = useState<{ src: string; w: number; h: number } | null>(null);
  const [profileCrop, setProfileCrop] = useState<{ src: string; w: number; h: number } | null>(null);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);

  // ── Hero URL input state (Paste URL feature) ──
  const [showHeroUrlInput, setShowHeroUrlInput] = useState(false);
  const [heroUrlDraft, setHeroUrlDraft] = useState('');

  // ── Hero photo menu ──
  const [heroMenuOpen, setHeroMenuOpen] = useState(false);
  const heroMenuRef = useRef<HTMLDivElement>(null);

  // ── Reference data ──
  const [communities, setCommunities] = useState<any[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [memberships, setMemberships] = useState<CommunityMembershipResponse[]>([]);

  // ── Home Community search (typeahead) ──
  const [locationQuery, setLocationQuery] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // ── Operational Community search (typeahead) ──
  const [opLocationQuery, setOpLocationQuery] = useState('');
  const [showOpLocationSuggestions, setShowOpLocationSuggestions] = useState(false);

  // (Account Settings modal moved to standalone AccountSettingsModal component)

  // ── Tabs ──
  const [activeTab, setActiveTab] = useState(0);
  // DEV-991 — Honor an `initialTab` (by key) passed via location.state when
  // arriving from a deep-link (e.g. clicking the owner avatar on a project's
  // hero). Applied once after `tabs` is computed, since we need the index
  // of the matching key. A ref prevents re-applying on subsequent renders
  // so the user can freely switch tabs after landing.
  const initialTabHandledRef = useRef(false);
  const initialTabKey = (location.state as any)?.initialTab as string | undefined;

  // Hidden file inputs
  const heroInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);

  // Fetch user + reference data
  useEffect(() => {
    if (!targetGuid) { setError('No user specified.'); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchUserByGuid(targetGuid),
      fetchAllCommunityEntitiesAdmin(),
      fetchAllRelationships(),
      fetchAllCommunityMemberships(),
    ])
      .then(([userData, communityData, relationshipData, membershipData]) => {
        if (cancelled) return;
        setUser(userData);
        setFormName(userData.name || '');
        setFormDisplayName(userData.displayName || '');
        setFormEmail(userData.email || '');
        setFormNotes(userData.notes || '');
        setFormPrimaryLocation(userData.primaryLocationEntityGuid || '');
        setFormScreenName(userData.screenName || '');
        setFormCountryCode(userData.countryCode || '');
        setFormOperationalLocation(userData.operationalCommunityGuid || '');
        setFormShowHome(userData.showHomeCommunity ?? false);
        setFormShowOp(userData.showOperationalCommunity ?? false);
        setFormIsSearchable(userData.isSearchable ?? false);
        setHeroUrl(mediaSrc(userData.heroImageUrl) ?? '');
        setProfileUrl(mediaSrc(userData.profileImageUrl) ?? '');
        setCommunities(communityData.map(communityEntityToSeedShape));
        setRelationships(relationshipData.map(relationshipToSeedShape));
        setMemberships(membershipData);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load user profile.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [targetGuid]);

  // DEV-1430 — refresh when Account Settings (a separate modal with its own
  // copy of the profile) saves changes to THIS user, so the page reflects a
  // name/profile edit immediately instead of only after a navigate-away/back
  // remount. Mirrors the l4e_calendar_changed refresh-event pattern.
  useEffect(() => {
    if (!targetGuid) return;
    const onProfileUpdated = (e: Event) => {
      const changed = String((e as CustomEvent).detail?.userGuid || '').toUpperCase();
      if (changed && changed !== targetGuid.toUpperCase()) return; // not this profile
      fetchUserByGuid(targetGuid)
        .then((u) => setUser(u))
        .catch(() => { /* keep current view on failure */ });
    };
    window.addEventListener('l4e_profile_updated', onProfileUpdated);
    return () => window.removeEventListener('l4e_profile_updated', onProfileUpdated);
  }, [targetGuid]);

  // ── Probe profile image URL (detect expired Google CDN URLs) ──
  useEffect(() => {
    if (!profileUrl) { setProfileImgValid(false); return; }
    // Internal API paths are always valid — only probe external URLs
    if (profileUrl.startsWith('/api/') || profileUrl.startsWith('blob:')) {
      setProfileImgValid(true);
      return;
    }
    const img = new Image();
    img.onload = () => setProfileImgValid(true);
    img.onerror = () => setProfileImgValid(false);
    img.src = profileUrl;
  }, [profileUrl]);

  // ── Close hero menu on click outside ──
  useEffect(() => {
    if (!heroMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (heroMenuRef.current && !heroMenuRef.current.contains(e.target as Node)) {
        setHeroMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [heroMenuOpen]);

  // ── Screen name debounced check ──
  const screenNameTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!formScreenName.trim() || formScreenName === (user?.screenName || '')) {
      setScreenNameStatus('idle');
      return;
    }
    setScreenNameStatus('checking');
    clearTimeout(screenNameTimer.current);
    screenNameTimer.current = setTimeout(async () => {
      const available = await checkScreenNameAvailable(formScreenName, targetGuid);
      setScreenNameStatus(available ? 'available' : 'taken');
    }, 500);
    return () => clearTimeout(screenNameTimer.current);
  }, [formScreenName, user?.screenName, targetGuid]);

  // ── Derived ──
  const userMemberships = useMemo(() => {
    if (!targetGuid) return [];
    return memberships.filter((m) => m.userGuid.toUpperCase() === targetGuid.toUpperCase());
  }, [memberships, targetGuid]);

  const communityMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const c of communities) map.set((c.entity_guid || '').toUpperCase(), c);
    return map;
  }, [communities]);

  /** Derive ISO 3166-1 alpha-2 country code from a community entity GUID by walking the hierarchy */
  const deriveCountryCode = useCallback((guid: string | null | undefined): string | null => {
    if (!guid) return null;
    const path = pathFromLeafGuid(guid, communities, relationships);
    for (const node of path) {
      const full = communityMap.get((node.entity_guid || '').toUpperCase());
      if (full && full.entity_type === 'geo_country' && full.code) return full.code;
    }
    return null;
  }, [communities, relationships, communityMap]);

  // Geo index for typeahead search (same pattern as ProjectConfigModal)
  const isGeoEntity = (e: any): boolean => String(e?.entity_type ?? '').toLowerCase().startsWith('geo_');

  const getGeoPathDisplay = useCallback((guid: string) => {
    const g = (guid || '').trim().toUpperCase();
    if (!g) return '';
    const leaf = communities.find((e: any) => (e.entity_guid || '').toUpperCase() === g);
    if (!leaf || !isGeoEntity(leaf)) return '';
    const rootToLeaf = pathFromLeafGuid(g, communities, relationships);
    const parts = (rootToLeaf ?? []).map((p: any) => String(p?.name ?? '').trim()).filter(Boolean);
    // Reverse to Leaf → Root, then strip "Earth" from the end
    const reversed = parts.reverse().filter((p: string) => p.toLowerCase() !== 'earth');
    return pathDisplay(reversed);
  }, [communities, relationships]);

  const geoIndex = useMemo(() => {
    const out: { guid: string; display: string; search: string; leafName: string }[] = [];
    for (const c of communities) {
      if (!isGeoEntity(c)) continue;
      const g = (c.entity_guid || '').toUpperCase();
      if (!g) continue;
      const leafName = String(c.name || '').trim();
      const display = getGeoPathDisplay(g);
      if (!display) continue;
      out.push({ guid: g, display, search: `${leafName} ${display}`.toLowerCase(), leafName });
    }
    out.sort((a, b) => a.display.localeCompare(b.display));
    return out;
  }, [communities, getGeoPathDisplay]);

  const locationSuggestions = useMemo(() => {
    const q = locationQuery.trim().toLowerCase();
    if (!q) return [] as { guid: string; display: string }[];
    const scored: { guid: string; display: string; score: number }[] = [];
    for (const row of geoIndex) {
      const name = row.leafName.toLowerCase();
      let score = -1;
      if (name.startsWith(q)) score = 100;
      else if (row.search.startsWith(q)) score = 90;
      else if (row.search.includes(` ${q}`)) score = 70;
      else if (row.search.includes(q)) score = 50;
      if (score < 0) continue;
      const depthBoost = Math.min(20, row.display.split(',').length);
      scored.push({ guid: row.guid, display: row.display, score: score + depthBoost });
    }
    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 12);
  }, [locationQuery, geoIndex]);

  const getCommunityName = useCallback(
    (guid: string) => communityMap.get(guid.toUpperCase())?.name || guid,
    [communityMap],
  );

  const selectedLocationLabel = useMemo(() => {
    if (!formPrimaryLocation) return '';
    return getGeoPathDisplay(formPrimaryLocation) || getCommunityName(formPrimaryLocation);
  }, [formPrimaryLocation, getGeoPathDisplay, getCommunityName]);

  // Operational community suggestions — reuse geoIndex with separate query
  const opLocationSuggestions = useMemo(() => {
    const q = opLocationQuery.trim().toLowerCase();
    if (!q) return [] as { guid: string; display: string }[];
    const scored: { guid: string; display: string; score: number }[] = [];
    for (const row of geoIndex) {
      const name = row.leafName.toLowerCase();
      let score = -1;
      if (name.startsWith(q)) score = 100;
      else if (row.search.startsWith(q)) score = 90;
      else if (row.search.includes(` ${q}`)) score = 70;
      else if (row.search.includes(q)) score = 50;
      if (score < 0) continue;
      const depthBoost = Math.min(20, row.display.split(',').length);
      scored.push({ guid: row.guid, display: row.display, score: score + depthBoost });
    }
    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 12);
  }, [opLocationQuery, geoIndex]);

  const selectedOpLocationLabel = useMemo(() => {
    if (!formOperationalLocation) return '';
    return getGeoPathDisplay(formOperationalLocation) || getCommunityName(formOperationalLocation);
  }, [formOperationalLocation, getGeoPathDisplay, getCommunityName]);

  const initials = useMemo(() => {
    const name = formDisplayName || formName || '?';
    return name.split(/\s+/).map((w) => w.charAt(0).toUpperCase()).slice(0, 2).join('');
  }, [formDisplayName, formName]);

  // ── Image file handler ──
  const handleImageFile = useCallback((file: File, target: 'hero' | 'profile') => {
    if (file.size > MAX_RAW_FILE_BYTES) {
      alert(`Image too large (max ${MAX_RAW_FILE_BYTES / 1024 / 1024}MB).`);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (target === 'hero') {
        setHeroCrop({ src: url, w: img.naturalWidth, h: img.naturalHeight });
      } else {
        setProfileCrop({ src: url, w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    img.src = url;
  }, []);

  // ── Upload cropped image to API ──
  const persistImage = useCallback(async (dataUrl: string, category: string, prefix: string) => {
    if (!targetGuid) return '';
    const ts = Date.now();
    try {
      const result = await uploadDataUrl(dataUrl, category, targetGuid, `${prefix}-${ts}.${category.includes('profile') ? 'png' : 'jpg'}`);
      return `${fileUrl(result.key)}?v=${ts}`;
    } catch (err) {
      console.error(`[UserProfilePage] Upload failed (${category}):`, err);
      return dataUrl;
    }
  }, [targetGuid]);

  // ── Inline bio save (About tab) ──
  const handleBioSave = useCallback(async () => {
    if (!targetGuid || bioBusy) return;
    setBioBusy(true);
    try {
      const updated = await updateUserProfile(targetGuid, { notes: bioDraft.trim() });
      setUser(updated);
      setFormNotes(updated.notes || '');
      setBioEditing(false);
    } catch (err: any) {
      window.alert(err?.message || 'Failed to save bio.');
    } finally {
      setBioBusy(false);
    }
  }, [targetGuid, bioBusy, bioDraft]);

  // ── Hero crop confirm ──
  const handleHeroCropConfirm = useCallback(async (dataUrl: string) => {
    setHeroCrop(null);
    const url = await persistImage(dataUrl, 'user-heroes', 'hero');
    setHeroUrl(url);
    if (targetGuid) {
      try {
        const updated = await updateUserProfile(targetGuid, { heroImageUrl: url });
        setUser(updated);
      } catch { /* ignore */ }
    }
  }, [targetGuid, persistImage]);

  // ── Profile crop confirm ──
  const handleProfileCropConfirm = useCallback(async (dataUrl: string) => {
    setProfileCrop(null);
    const url = await persistImage(dataUrl, 'user-profiles', 'profile');
    setProfileUrl(url);
    if (targetGuid) {
      try {
        const updated = await updateUserProfile(targetGuid, { profileImageUrl: url });
        setUser(updated);
      } catch { /* ignore */ }
    }
  }, [targetGuid, persistImage]);

  // ── Hero: Edit Crop (re-crop existing hero image) ──
  // Fetches as blob to avoid canvas taint from cross-origin image URLs.
  const handleEditHeroCrop = useCallback(() => {
    if (!heroUrl) return;
    fetch(heroUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          setHeroCrop({ src: blobUrl, w: img.naturalWidth, h: img.naturalHeight });
        };
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          alert('Could not load the current hero image for editing.');
        };
        img.src = blobUrl;
      })
      .catch(() => { alert('Could not load the current hero image for editing.'); });
  }, [heroUrl]);

  // ── Hero: Paste URL submit ──
  const handleHeroUrlSubmit = useCallback(async () => {
    const url = heroUrlDraft.trim();
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      alert('Please enter a valid URL starting with http:// or https://');
      return;
    }
    if (url.startsWith('data:') && targetGuid) {
      try {
        const result = await uploadDataUrl(url, 'user-heroes', targetGuid, 'hero.jpg');
        const storedUrl = `${fileUrl(result.key)}?v=${Date.now()}`;
        setHeroUrl(storedUrl);
        const updated = await updateUserProfile(targetGuid, { heroImageUrl: storedUrl });
        setUser(updated);
      } catch {
        setHeroUrl(url);
      }
    } else {
      setHeroUrl(url);
      if (targetGuid) {
        try {
          const updated = await updateUserProfile(targetGuid, { heroImageUrl: url });
          setUser(updated);
        } catch { /* ignore */ }
      }
    }
    setShowHeroUrlInput(false);
    setHeroUrlDraft('');
  }, [heroUrlDraft, targetGuid]);

  // ── Hero: Remove ──
  const handleRemoveHero = useCallback(async () => {
    setHeroUrl('');
    if (targetGuid) {
      try {
        const updated = await updateUserProfile(targetGuid, { heroImageUrl: '' });
        setUser(updated);
      } catch { /* ignore */ }
    }
  }, [targetGuid]);

  // Dual flag derivation — computed before tabs so they can reference homeCountryCode
  const homeCountryCode = deriveCountryCode(
    formPrimaryLocation || user?.primaryLocationEntityGuid
  ) || user?.countryCode || null;
  const opCountryCode = deriveCountryCode(
    formOperationalLocation || user?.operationalCommunityGuid
  );
  const homeFlag = countryFlag(homeCountryCode);
  const opFlag = countryFlag(opCountryCode);
  const showHomeFlag = (formShowHome && !!homeFlag) || false;
  const showOpFlag = (formShowOp && !!opFlag && opCountryCode !== homeCountryCode) || false;
  const flag = homeFlag;
  const displayHandle = user?.screenName ? `@${user.screenName}` : null;

  // ── §7.17 / DEV-1484 — completion guidance state (declared BEFORE the
  // tabs memo, whose bio section takes the ref + ring class). LIVE
  // predicate over the page's own state (uploads and bio saves clear
  // rings the moment they land, no refetch wait); mirrors the singular
  // server predicate (avatar + cover + bio) via profileCompletion. ──
  const completion = useMemo(
    () =>
      profileCompletion({
        profile_image_url: profileUrl,
        hero_image_url: heroUrl,
        notes: formNotes || user?.notes || '',
      }),
    [profileUrl, heroUrl, formNotes, user],
  );
  // Finish moment fires only when the line is crossed DURING this visit.
  const startedIncompleteRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isOwnProfile || loading || !user) return;
    if (startedIncompleteRef.current === null) {
      startedIncompleteRef.current = !completion.complete;
    }
  }, [isOwnProfile, loading, user, completion.complete]);
  const completionJustFinished =
    completion.complete && startedIncompleteRef.current === true;
  const avatarHintRef = useRef<HTMLDivElement | null>(null);
  const bioSectionRef = useRef<HTMLDivElement | null>(null);
  // Guidance is active only while own-profile + incomplete (§7.17 r5).
  const guidanceActive = isOwnProfile && !completion.complete;

  // ── Tab definitions ──
  const tabs: TabDefinition[] = useMemo(() => [
    {
      key: 'about',
      label: t('userProfile:tabs.about'),
      content: (
        <div style={{ paddingTop: 16 }}>
          {/* ── Short bio — edited IN PLACE (Skip, 2026-07-07). Previously
              only editable at the bottom of Account Settings, where nobody
              found it despite complete_profile requiring it.
              §7.17: quiet hint ring while the bio is the missing item. ── */}
          <div
            style={ST.section}
            ref={bioSectionRef}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={ST.sectionTitle}>{t('userProfile:aboutBio.title')}</div>
              {isOwnProfile && !bioEditing && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {guidanceActive && !completion.bio && (
                    <ProfileHintArrow
                      label={t('userProfile:completion.addBioTag')}
                      points="inlineEnd"
                      onClick={() => {
                        setBioDraft(formNotes || user?.notes || '');
                        setBioEditing(true);
                      }}
                    />
                  )}
                  <button
                    type="button"
                    aria-label={t('userProfile:aboutBio.edit')}
                    title={t('userProfile:aboutBio.edit')}
                    onClick={() => {
                      setBioDraft(formNotes || user?.notes || '');
                      setBioEditing(true);
                    }}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', padding: 4, lineHeight: 1,
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            {bioEditing ? (
              <div>
                <textarea
                  value={bioDraft}
                  onChange={(e) => setBioDraft(e.target.value)}
                  placeholder={t('userProfile:aboutBio.placeholder')}
                  rows={4}
                  maxLength={2000}
                  autoFocus
                  disabled={bioBusy}
                  style={{
                    inlineSize: '100%', boxSizing: 'border-box', resize: 'vertical',
                    background: 'var(--bg-primary)', color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)', borderRadius: 6,
                    padding: '8px 10px', fontSize: 14, lineHeight: 1.6,
                  }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button
                    type="button"
                    disabled={bioBusy}
                    onClick={() => setBioEditing(false)}
                    style={{
                      background: 'transparent', border: '1px solid var(--border-default)',
                      color: 'var(--text-secondary)', borderRadius: 6, padding: '6px 12px',
                      fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    {t('userProfile:aboutBio.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={bioBusy}
                    onClick={handleBioSave}
                    style={{
                      background: 'var(--info-text)', border: 'none',
                      color: 'var(--bg-primary)', borderRadius: 6, padding: '6px 14px',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {t('userProfile:aboutBio.save')}
                  </button>
                </div>
              </div>
            ) : (formNotes || (user?.notes)) ? (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {formNotes || user?.notes}
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                {isOwnProfile
                  ? t('userProfile:aboutBio.emptyOwn')
                  : t('userProfile:aboutBio.emptyOther')}
              </p>
            )}
          </div>

          {/* ── Public Details ── */}
          <div style={ST.section}>
            <div style={ST.sectionTitle}>Details</div>
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
              {user?.screenName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                  <span style={{ color: 'var(--accent-primary)', fontWeight: 500 }}>@{user.screenName}</span>
                </div>
              )}
              {formPrimaryLocation && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                  </svg>
                  <span style={{ color: 'var(--text-secondary)' }}>{getCommunityName(formPrimaryLocation)}</span>
                </div>
              )}
              {homeCountryCode && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                  <span style={{ width: 20, display: 'inline-flex', justifyContent: 'center' }}>
                    <CountryFlag code={homeCountryCode} size={18} radius={2} />
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {COUNTRY_OPTIONS.find(c => c.code === homeCountryCode)?.name || homeCountryCode}
                  </span>
                </div>
              )}
              {!user?.screenName && !formPrimaryLocation && !homeCountryCode && (
                <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {isOwnProfile
                    ? 'Add your details in Account Settings.'
                    : 'No public details available.'}
                </p>
              )}
            </div>
          </div>

          {/* ── Community Memberships ── */}
          <div style={ST.section}>
            <div style={ST.sectionTitle}>Community Memberships ({userMemberships.length})</div>
            {userMemberships.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>No community memberships yet.</div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {userMemberships.map((m) => {
                  const ancestors = resolveCommunityAncestors(m.communityEntityGuid);
                  const communityName = getCommunityName(m.communityEntityGuid);
                  const ancestorPath = [...ancestors].reverse();
                  return (
                    <li key={`${m.communityEntityGuid}-${m.membershipType}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 14, color: 'var(--text-primary)' }}>
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 0, flexWrap: 'wrap' as const }}>
                        <button
                          type="button"
                          onClick={() => navigate(`/communities/${m.communityEntityGuid}`)}
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 15, fontWeight: 600, color: 'var(--accent-text, var(--accent-bg))', cursor: 'pointer', textDecoration: 'none' }}
                        >
                          {communityName}
                        </button>
                        {ancestorPath.length > 0 && (
                          <span style={{ fontSize: 15, color: 'var(--text-tertiary)' }}>
                            {ancestorPath.map((a, i) => {
                              const entity = communityConfigStore.get(a.guid.toUpperCase());
                              const isActive = entity?.is_active !== false;
                              return (
                                <React.Fragment key={a.guid}>
                                  <span>{',\u00A0'}</span>
                                  {isActive ? (
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/communities/${a.guid}`)}
                                      style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 15, color: 'var(--accent-text, var(--accent-bg))', cursor: 'pointer', textDecoration: 'none' }}
                                    >
                                      {a.name}
                                    </button>
                                  ) : (
                                    <span>{a.name}</span>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </span>
                        )}
                      </span>
                      <span style={ST.badge}>{m.membershipType}</span>
                      <span style={ST.badge}>{m.status}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ── Skills (User Capability Registry — DEV-1378) ── */}
          <ProfileSkillsSection targetGuid={targetGuid} isOwnProfile={isOwnProfile} />
        </div>
      ),
    },
    {
      key: 'knowledge',
      label: t('userProfile:tabs.knowledge'),
      content: (
        <KnowledgeTab
          userGuid={user?.userGuid || ''}
          isOwnProfile={isOwnProfile}
          displayName={user?.displayName || user?.screenName || ''}
        />
      ),
    },
    {
      key: 'blog',
      label: t('userProfile:tabs.blog'),
      content: (
        <BlogTab
          userGuid={user?.userGuid || ''}
          isOwnProfile={isOwnProfile}
          displayName={user?.displayName || user?.screenName || ''}
        />
      ),
    },
    {
      key: 'photos',
      label: t('userProfile:tabs.photos'),
      content: (
        <div style={{ padding: '24px 0' }}>
          <ProgressGallery
            entityType="user"
            entityGuid={targetGuid}
            canUpload={isOwnProfile}
            titleOverride={t('userProfile:gallery.title')}
            emptyTitleOverride={t('userProfile:gallery.noPhotos')}
            emptyHintOverride={
              isOwnProfile
                ? t('userProfile:gallery.noPhotosOwn')
                : t('userProfile:gallery.noPhotosOther')
            }
          />
        </div>
      ),
    },
    {
      key: 'feed',
      label: t('userProfile:tabs.feed'),
      content: (
        <div style={{ padding: '24px 0' }}>
          {/* ── Feed Filters ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 20, gap: 12, flexWrap: 'wrap' as const,
          }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {['All Activity', 'Projects', 'Community', 'Achievements'].map((filter, i) => (
                <button key={filter} type="button" style={{
                  background: i === 0 ? 'rgba(69,187,71,0.15)' : 'var(--bg-hover)',
                  border: `1px solid ${i === 0 ? 'rgba(69,187,71,0.3)' : 'var(--border-subtle)'}`,
                  borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500,
                  color: i === 0 ? '#45BB47' : 'var(--text-tertiary)', cursor: 'pointer',
                }}>{filter}</button>
              ))}
            </div>
          </div>

          {/* ── Activity Event Types Legend ── */}
          <div style={ST.section}>
            <div style={ST.sectionTitle}>Activity Types</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 12 }}>
              {[
                { label: 'Project Update', color: 'var(--accent-primary)', icon: 'M3 3h18v18H3zM3 9h18M9 21V9' },
                { label: 'KPI Milestone', color: 'var(--color-warning)', icon: 'M12 2L2 7l10 5 10-5-10-5z' },
                { label: 'Community', color: '#06b6d4', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' },
                { label: 'Badge Earned', color: 'var(--accent-secondary)', icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' },
                { label: 'Article Published', color: '#45BB47', icon: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z' },
              ].map(({ label, color, icon }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
                  borderRadius: 6, padding: '6px 12px',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                    <path d={icon} />
                  </svg>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Empty Feed ── */}
          <div style={ST.section}>
            <div style={{
              display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
              padding: '48px 16px', color: 'var(--text-muted)',
            }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5">
                <path d="M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16" />
                <circle cx="5" cy="19" r="1" />
              </svg>
              <h3 style={{ margin: '16px 0 8px', fontSize: 17, fontWeight: 600, color: 'var(--text-tertiary)' }}>
                {isOwnProfile ? 'Your activity will appear here' : 'No recent activity'}
              </h3>
              <p style={{ margin: 0, fontSize: 13, textAlign: 'center' as const, maxWidth: 400, lineHeight: 1.5 }}>
                {isOwnProfile
                  ? 'As you participate in projects, engage with communities, earn badges, and publish articles, your activity will be tracked here.'
                  : 'This user hasn\'t generated any public activity yet.'}
              </p>
            </div>
          </div>

          {/* Feed items will render as a chronological stream: icon, timestamp, description, linked entity */}
        </div>
      ),
    },

  ], [user, isOwnProfile, t, homeCountryCode, formPrimaryLocation, targetGuid,
      // Inline bio editor (Skip 2026-07-07) + previously-omitted stable refs
      // surfaced by exhaustive-deps once the memo body grew.
      bioEditing, bioDraft, bioBusy, formNotes, handleBioSave,
      getCommunityName, navigate, userMemberships,
      // §7.17 guidance inputs (bio hint ring).
      guidanceActive, completion.bio]);

  // ── §7.17 / DEV-1484 — chip scroll-and-focus (bio lives on the About
  // tab — index by key, never position; switch first, scroll after). ──
  const focusCompletionItem = useCallback(
    (item: 'photo' | 'cover' | 'bio') => {
      if (item === 'photo') {
        avatarHintRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (item === 'cover') {
        heroMenuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const aboutIdx = tabs.findIndex((tt) => tt.key === 'about');
      if (aboutIdx >= 0 && aboutIdx !== activeTab) setActiveTab(aboutIdx);
      window.setTimeout(() => {
        bioSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    },
    [tabs, activeTab],
  );

  // ── Render ──
  if (loading) return <div style={ST.loading}>Loading user profile...</div>;
  if (error) return <div style={ST.error}>{error}</div>;
  if (!user) return <div style={ST.error}>User not found.</div>;

  return (
    <div
      ref={scrollRef}
      style={{
        color: 'var(--text-primary)',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
      }}
    >
      {/* ── §7.17 / DEV-1484 — profile-completion last-mile guidance.
          Own-profile + incomplete only; the strip component itself
          handles the finish moment and the disappears-forever rule. */}
      {isOwnProfile && (
        <div style={{ maxWidth: HERO_MAX_WIDTH, margin: '0 auto', width: '100%', paddingInline: 12, boxSizing: 'border-box' }}>
          <ProfileCompletionStrip
            state={completion}
            justCompleted={completionJustFinished}
            acBadgeCount={acBadgeCount ?? 0}
            onFocusItem={focusCompletionItem}
          />
        </div>
      )}

      {/* ── Hero Banner + Overlaid Profile Info ── */}
      <div style={{
        position: 'relative',
        width: '100%',
        overflow: heroMenuOpen ? 'visible' : 'hidden',
        flexShrink: 0,
      }}>
        {/* DEV-1452: in-app back button removed. */}

        {/* Blurred bleed background.
            DEV-1473 — suppressed in the light theme, like every other hero bleed
            (community, org, project, pillar shell). The brightness(0.35) filter
            is a DARKENING effect: it sinks a blurred copy of the hero into a dark
            page. On a light page it paints a grey smear in the margins and reads
            as a rendering artifact. This one is an inline style, so it can't be
            reached by the CSS rule the other five use — hence the hook. */}
        {heroUrl && !isLightTheme && (
          <div style={{
            position: 'absolute', inset: 0,
            background: `url(${heroUrl}) center/cover no-repeat`,
            filter: 'blur(44px) saturate(1.1) brightness(0.35)',
            transform: 'scale(1.15)',
            opacity: 0.75,
          }} />
        )}

        {/* Constrained hero photo — left Enabler blue border connects to tab bar */}
        <div style={{
          position: 'relative',
          maxWidth: HERO_MAX_WIDTH,
          height: heroH,
          margin: '0 auto',
          overflow: heroMenuOpen ? 'visible' : 'hidden',
          borderRadius: '12px 12px 0 0',
          borderInlineStart: '4px solid #3b82f6',
          background: heroUrl
            ? `url(${heroUrl}) center/${containerW < HERO_MAX_WIDTH ? '100% auto' : 'cover'} no-repeat`
            : 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)',
        }}>
          {/* Dark gradient scrim for overlaid text */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 40%, transparent 70%)',
          }} />

          {/* In-place crop overlay */}
          {heroCrop && (
            <HeroCropOverlay
              imageSrc={heroCrop.src}
              naturalW={heroCrop.w}
              naturalH={heroCrop.h}
              onConfirm={handleHeroCropConfirm}
              onCancel={() => setHeroCrop(null)}
            />
          )}

          {/* Hero edit controls */}
          {isOwnProfile && !heroCrop && (
            <div ref={heroMenuRef} style={{
              position: 'absolute', top: 10, insetInlineEnd: 10, zIndex: 50,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {guidanceActive && !completion.cover && (
                <ProfileHintArrow
                  label={t('userProfile:completion.addCoverTag')}
                  points="inlineEnd"
                  onClick={() => setHeroMenuOpen(true)}
                />
              )}
              <button
                type="button"
                onClick={() => setHeroMenuOpen((v) => !v)}
                style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', color: '#1e293b',
                border: 'none', borderRadius: 8, padding: '7px 14px',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                transition: 'background 0.15s',
              }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                Edit cover photo
              </button>

              {heroMenuOpen && (
                <div style={{
                  position: 'absolute', top: '100%', insetInlineEnd: 0,
                  marginTop: 6,
                  background: 'var(--bg-elevated)', backdropFilter: 'blur(8px)',
                  borderRadius: 10, overflow: 'hidden',
                  border: '1px solid var(--border-default)',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.5)', minWidth: 180,
                  zIndex: 60,
                }}>
                  <button type="button" onClick={() => { heroInputRef.current?.click(); setHeroMenuOpen(false); }} style={heroMenuItemStyle}>
                    {heroUrl ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {heroUrl && (
                    <button type="button" onClick={() => { handleEditHeroCrop(); setHeroMenuOpen(false); }} style={heroMenuItemStyle}>
                      Edit Crop
                    </button>
                  )}
                  <button type="button" onClick={() => {
                    setShowHeroUrlInput((v) => !v); setHeroUrlDraft(''); setHeroMenuOpen(false);
                  }} style={heroMenuItemStyle}>
                    Paste URL
                  </button>
                  {heroUrl && (
                    <button type="button" onClick={() => { handleRemoveHero(); setHeroMenuOpen(false); }} style={{
                      ...heroMenuItemStyle, color: '#f87171', borderBottom: 'none',
                    }}>
                      Remove Photo
                    </button>
                  )}
                </div>
              )}

              {showHeroUrlInput && (
                <div style={{
                  position: 'absolute', top: '100%', insetInlineEnd: 0,
                  marginTop: 6, display: 'flex', gap: 6, maxWidth: 420,
                }}>
                  <input
                    type="text"
                    value={heroUrlDraft}
                    onChange={(e) => setHeroUrlDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleHeroUrlSubmit();
                      if (e.key === 'Escape') { setShowHeroUrlInput(false); setHeroUrlDraft(''); }
                    }}
                    placeholder="https://example.com/image.jpg"
                    autoFocus
                    style={{
                      flex: 1, background: 'rgba(0,0,0,0.7)', border: '1px solid var(--border-default)',
                      borderRadius: 6, padding: '5px 10px', fontSize: 12, color: 'var(--text-primary)',
                      outline: 'none', backdropFilter: 'blur(6px)', minWidth: 200,
                    }}
                  />
                  <button type="button" onClick={handleHeroUrlSubmit} style={{
                    background: '#fff', color: '#1e293b', border: 'none', borderRadius: 6,
                    padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>Save</button>
                  <button type="button" onClick={() => { setShowHeroUrlInput(false); setHeroUrlDraft(''); }} style={{
                    background: 'rgba(255,255,255,0.85)', color: '#1e293b', border: 'none',
                    borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>Cancel</button>
                </div>
              )}
            </div>
          )}

          {/* ── Overlaid profile info — Option B layout ── */}
          <div style={{
            position: 'absolute',
            insetBlockEnd: 0,
            insetInline: 0,
            padding: '0 24px 10px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0,
            maxWidth: 1080,
            marginInline: 'auto',
            zIndex: 5,
          }}>
            {/* Avatar wrapper — sized to full swoosh extent so layout accounts for it */}
            <div style={{
              position: 'relative',
              width: PROFILE_FULL,
              height: PROFILE_FULL,
              flexShrink: 0,
              marginInlineStart: -25,
            }}>
              {/* Avatar + arc centered inside at SWOOSH_PAD offset.
                  §7.17: hint ring + "Add your photo" tag while missing. */}
              <div
                ref={avatarHintRef}
                style={{
                position: 'absolute',
                top: SWOOSH_PAD,
                insetInlineStart: SWOOSH_PAD,
                width: PROFILE_SIZE,
                height: PROFILE_SIZE,
                borderRadius: '50%',
              }}>
                <ProfileArc size={PROFILE_SIZE} collapsed={false} isEnabler={ringIsEnabler} />
                {guidanceActive && !completion.photo && (
                  <>
                    <span
                      onClick={() => setAvatarPickerOpen(true)}
                      style={{
                        position: 'absolute', insetInlineEnd: 2, insetBlockEnd: 2,
                        width: 30, height: 30, borderRadius: '50%',
                        background: 'var(--accent-primary)', color: 'var(--bg-primary)',
                        border: '3px solid var(--bg-primary)',
                        display: 'grid', placeItems: 'center', cursor: 'pointer', zIndex: 3,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      }}
                      aria-hidden="true"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                    </span>
                    <ProfileHintArrow
                      label={t('userProfile:completion.addPhotoTag')}
                      points="inlineStart"
                      onClick={() => setAvatarPickerOpen(true)}
                      style={{ position: 'absolute', insetInlineStart: 'calc(100% + 8px)', insetBlockStart: '60%', transform: 'translateY(-50%)' }}
                    />
                  </>
                )}
                <div
                  style={{
                    width: PROFILE_SIZE,
                    height: PROFILE_SIZE,
                    borderRadius: '50%',
                    border: '3px solid var(--border-strong)',
                    background: profileUrl && profileImgValid ? `url(${profileUrl}) center/cover no-repeat` : 'var(--bg-tertiary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: Math.round(PROFILE_SIZE * 0.35), fontWeight: 700, color: 'var(--text-tertiary)',
                    cursor: isOwnProfile ? 'pointer' : 'default',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onClick={isOwnProfile ? () => setAvatarPickerOpen(true) : undefined}
                  title={isOwnProfile ? 'Click to change profile photo' : undefined}
                >
                  {(!profileUrl || !profileImgValid) && initials}
                  {isOwnProfile && (
                    <div style={{
                      position: 'absolute', inset: 0, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.4)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      opacity: 0, transition: 'opacity 0.15s',
                    }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0'; }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Flag badge — top-right of full wrapper */}
              {(() => {
                const sameCountry = homeCountryCode && opCountryCode && homeCountryCode === opCountryCode;
                const primaryCode = sameCountry && formShowOp ? opCountryCode : homeCountryCode;
                // DEV-1487 — "flag or nothing." The badge shows ONLY when the
                // user has enabled sharing. The old `|| (isOwnProfile && ...)`
                // bypass made your own flag appear even with sharing OFF — so the
                // one profile you look at most (your own) contradicted the toggle
                // and every other viewer. Removed: off is off, for everyone.
                // Flags render as bundled SVGs (CountryFlag), not emoji, so
                // off-Mac viewers see the flag rather than the ISO letters.
                const showPrimary = showHomeFlag || showOpFlag;
                if (!showPrimary) return null;
                return (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    insetInlineEnd: 0,
                    lineHeight: 1,
                    zIndex: 5,
                  }}>
                    <CountryFlag code={primaryCode} size={44} radius={5} shadow />
                    {showOpFlag && (
                      <div style={{
                        position: 'absolute',
                        bottom: -8,
                        insetInlineEnd: -10,
                        lineHeight: 1,
                      }}>
                        <CountryFlag code={opCountryCode} size={32} radius={4} shadow />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Name / handle / location — text block */}
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 16, paddingInlineEnd: 28, marginInlineStart: 7, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h1 style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {user.displayName || user.name || 'Unnamed User'}
                </h1>
                {/* Online indicator — inline next to name */}
                {isOnline && (
                  <span style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#22c55e',
                    boxShadow: '0 0 4px rgba(34, 197, 94, 0.6)',
                    flexShrink: 0,
                  }} />
                )}
                {user.isSystemAdmin && (
                  <span style={{
                    background: '#7c3aed', color: '#fff', borderRadius: 4,
                    padding: '2px 8px', fontSize: 11, fontWeight: 700,
                  }}>ADMIN</span>
                )}
              </div>
              {displayHandle && (
                <div style={{ fontSize: 14, color: 'rgba(147,197,253,0.95)', fontWeight: 500, marginTop: 2 }}>{displayHandle}</div>
              )}
              <div style={{ fontSize: 15, color: 'rgba(203,213,225,0.9)', marginTop: displayHandle ? 2 : 4, display: 'flex', flexWrap: 'wrap' as const, alignItems: 'baseline', gap: 0 }}>
                {(() => {
                  const locationGuid = (formShowOp && formOperationalLocation)
                    ? formOperationalLocation
                    : (formShowHome && formPrimaryLocation)
                      ? formPrimaryLocation
                      : null;
                  if (!locationGuid) return <span>Earth</span>;

                  const communityName = getCommunityName(locationGuid);
                  const ancestors = resolveCommunityAncestors(locationGuid);
                  const ancestorPath = [...ancestors].reverse();

                  const linkColor = 'rgba(147,197,253,0.95)';
                  const textColor = 'rgba(203,213,225,0.6)';
                  const linkStyle: React.CSSProperties = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 15, fontWeight: 500, color: linkColor, cursor: 'pointer', textDecoration: 'none' };

                  return (
                    <>
                      <button type="button" onClick={() => navigate(`/communities/${locationGuid}`)} style={linkStyle}>
                        {communityName}
                      </button>
                      {ancestorPath.map((a) => {
                        const entity = communityConfigStore.get(a.guid.toUpperCase());
                        const isActive = entity?.is_active !== false;
                        return (
                          <React.Fragment key={a.guid}>
                            <span style={{ color: textColor }}>{',\u00A0'}</span>
                            {isActive ? (
                              <button type="button" onClick={() => navigate(`/communities/${a.guid}`)} style={linkStyle}>
                                {a.name}
                              </button>
                            ) : (
                              <span style={{ color: textColor }}>{a.name}</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
              {!displayHandle && !formPrimaryLocation && !formOperationalLocation && (
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                  {isOwnProfile ? 'Your Profile' : 'User Profile'}
                </div>
              )}
            </div>

            {/* Contact / Follow buttons — overlaid on hero, right side */}
            {!isOwnProfile && contactStatus && (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, paddingBottom: 16, paddingInlineEnd: 28 }}>
                {contactStatus.status === 'none' && (
                  <button type="button" onClick={handleRequestContact} disabled={contactLoading} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'var(--accent-primary)', border: '1px solid var(--accent-primary)',
                    borderRadius: 8, padding: '7px 16px',
                    fontSize: 13, fontWeight: 600, color: '#fff',
                    cursor: contactLoading ? 'wait' : 'pointer',
                    transition: 'opacity 0.15s', opacity: contactLoading ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="8.5" cy="7" r="4" />
                      <line x1="20" y1="8" x2="20" y2="14" />
                      <line x1="23" y1="11" x2="17" y2="11" />
                    </svg>
                    Request Contact
                  </button>
                )}
                {contactStatus.status === 'pending' && contactStatus.direction === 'outgoing' && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 8, padding: '7px 16px',
                    fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)',
                    whiteSpace: 'nowrap',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    Request Pending
                  </span>
                )}
                {contactStatus.status === 'accepted' && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
                    border: '1px solid var(--status-success)',
                    borderRadius: 8, padding: '7px 16px',
                    fontSize: 13, fontWeight: 600, color: 'var(--status-success)',
                    whiteSpace: 'nowrap',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Connected
                  </span>
                )}
                {contactStatus.status === 'blocked_by_me' && (
                  <button type="button" onClick={handleUnblockFromProfile} disabled={contactLoading} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
                    border: '1px solid var(--status-error)',
                    borderRadius: 8, padding: '7px 16px',
                    fontSize: 13, fontWeight: 600, color: 'var(--status-error)',
                    cursor: contactLoading ? 'wait' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}>
                    Unblock
                  </button>
                )}
                {contactStatus.status !== 'blocked_by_me' && contactStatus.status !== 'blocked_by_them' && (
                  <FollowButton targetType="user" targetGuid={targetGuid} showCount={false} />
                )}
                {viewerIsSystemAdmin && (
                  <button type="button" onClick={() => setShowAdminGrant(true)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
                    border: '1px solid var(--accent-primary, #7c3aed)',
                    borderRadius: 8, padding: '7px 16px',
                    fontSize: 13, fontWeight: 600, color: 'var(--accent-primary, #7c3aed)',
                    cursor: 'pointer', transition: 'opacity 0.15s',
                    whiteSpace: 'nowrap',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    Grant Credit
                  </button>
                )}
                {contactStatus.status === 'accepted' && (
                  <div style={{ position: 'relative' }}>
                    <button type="button" onClick={(e) => {
                      const menu = e.currentTarget.nextElementSibling as HTMLElement;
                      if (menu) menu.style.display = menu.style.display === 'none' ? 'flex' : 'none';
                    }} style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8, padding: '7px 10px',
                      cursor: 'pointer', color: 'rgba(255,255,255,0.7)',
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                      </svg>
                    </button>
                    <div style={{
                      display: 'none', position: 'absolute', insetBlockStart: '100%', insetInlineEnd: 0,
                      marginBlockStart: 4, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                      borderRadius: 8, boxShadow: 'var(--shadow-lg)', zIndex: 10,
                      flexDirection: 'column', minWidth: 150, overflow: 'hidden',
                    }}>
                      <button type="button" onClick={handleRemoveContact} style={{
                        padding: '10px 14px', background: 'none', border: 'none',
                        textAlign: 'start', fontSize: 13, color: 'var(--text-secondary)',
                        cursor: 'pointer',
                      }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                      >
                        Remove Contact
                      </button>
                      <button type="button" onClick={handleBlockFromProfile} style={{
                        padding: '10px 14px', background: 'none', border: 'none',
                        textAlign: 'start', fontSize: 13, color: 'var(--status-error)',
                        cursor: 'pointer',
                      }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                      >
                        Block User
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>{/* end constrained hero */}
      </div>{/* end hero outer */}

      {/* ── Sticky zone: mini header + tab bar ── */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        maxWidth: HERO_MAX_WIDTH,
        marginInline: 'auto',
      }}>
        {/* Mini sticky header — shows user avatar + name when scrolled past hero */}
        {scrolledPastHero && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '6px 24px',
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            <div style={{
              position: 'relative',
              width: PROFILE_SIZE_SM,
              height: PROFILE_SIZE_SM,
              flexShrink: 0,
            }}>
              <ProfileArc size={PROFILE_SIZE_SM} collapsed={true} isEnabler={ringIsEnabler} />
              <div style={{
                width: PROFILE_SIZE_SM,
                height: PROFILE_SIZE_SM,
                borderRadius: '50%',
                border: '2px solid var(--bg-tertiary)',
                background: profileUrl && profileImgValid ? `url(${profileUrl}) center/cover no-repeat` : 'var(--bg-tertiary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: 'var(--text-tertiary)',
              }}>
                {(!profileUrl || !profileImgValid) && initials}
              </div>
            </div>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.displayName || user.name || 'Unnamed User'}
            </span>
            {displayHandle && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{displayHandle}</span>
            )}
          </div>
        )}

        {/* DEV-991 — Honor initialTab from navigation state. Applied once
            after tabs is computed so we can resolve the key→index mapping.
            The ref prevents re-applying on subsequent renders, leaving the
            user free to switch tabs after landing. */}
        {(() => {
          if (initialTabHandledRef.current) return null;
          if (!initialTabKey || tabs.length === 0) return null;
          const idx = tabs.findIndex((tt) => tt.key === initialTabKey);
          if (idx >= 0 && idx !== activeTab) {
            initialTabHandledRef.current = true;
            // setActiveTab is safe inside render here? No — needs to be in
            // effect. Use queueMicrotask to defer the state update.
            queueMicrotask(() => setActiveTab(idx));
          } else if (idx >= 0) {
            initialTabHandledRef.current = true;
          }
          return null;
        })()}

        {/* Tab bar — stickyTabBar applies border + radius to .tabBar only */}
        <div style={{ '--pillar-accent': '#3b82f6' } as React.CSSProperties}>
          <ConfigurableTabSystem
            tabs={tabs}
            activeTabIndex={activeTab}
            onTabChange={setActiveTab}
            accentColor="#3b82f6"
            stickyTabBar
          />
        </div>
      </div>

      {/* Hidden file inputs */}
      <input ref={heroInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f, 'hero'); e.target.value = ''; }} />
      <input ref={profileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f, 'profile'); e.target.value = ''; }} />

      {/* Profile crop modal (circle — stays as full-page modal) */}
      <AvatarPickerModal
        open={avatarPickerOpen}
        onClose={() => setAvatarPickerOpen(false)}
        onUseGenerated={async (png, config) => {
          await handleProfileCropConfirm(png);
          if (targetGuid) {
            try {
              const u = await updateUserProfile(targetGuid, { avatarConfig: JSON.stringify(config) });
              setUser(u);
            } catch { /* ignore */ }
          }
        }}
        onUploadPhoto={() => profileInputRef.current?.click()}
        displayName={formDisplayName || user?.displayName || ''}
        handle={user?.screenName ? `@${user.screenName}` : undefined}
        initialConfig={(() => { try { return user?.avatarConfig ? JSON.parse(user.avatarConfig) : null; } catch { return null; } })()}
      />
      {profileCrop && (
        <CircleCropModal
          imageSrc={profileCrop.src}
          naturalW={profileCrop.w}
          naturalH={profileCrop.h}
          onConfirm={handleProfileCropConfirm}
          onCancel={() => setProfileCrop(null)}
        />
      )}

      {/* Account Settings modal is now rendered at App level via AccountSettingsModal */}


      {/* ── Tab Content ── */}
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px 48px' }}>
        {/* Tab content is rendered by ConfigurableTabSystem */}
      </div>

      {/* Admin Grant Impact Credit modal — system admins only */}
      {showAdminGrant && (
        <AdminGrantModal
          preselectedUserGuid={targetGuid}
          onClose={() => setShowAdminGrant(false)}
          onGranted={() => {/* could refetch impact data here */}}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/*  Theme Switcher — Account Settings appearance section                */
/* ================================================================== */

/* ── Shared styles for Account Settings dropdowns ── */
const SETTINGS_SELECT: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px solid var(--border-default)',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  paddingInlineEnd: 44,
};

const SETTINGS_LABEL: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6,
};

const SETTINGS_CARD: React.CSSProperties = {
  marginBottom: 14,
};

/* ================================================================== */
/*  Theme Switcher — dropdown                                          */
/* ================================================================== */

function ThemeSwitcher({ value, onChange, availableThemes }: { value: string; onChange: (v: string) => void; availableThemes: Array<{ name: string; isCustom: boolean; baseTheme?: 'dark' | 'light'; displayName?: string; icon?: string }> }) {
  const { t } = useTranslation();

  /** Icon for a theme entry. Uses admin override, else moon/sun from base. */
  const themeIcon = (theme: { name: string; isCustom: boolean; baseTheme?: string; icon?: string }) => {
    if (theme.icon) return theme.icon;
    const base = theme.isCustom ? theme.baseTheme : theme.name;
    return base === 'light' ? '\u2600\uFE0F' : '\uD83C\uDF19';
  };

  /** Display label. Uses admin displayName override, then i18n for built-in, then raw name. */
  const themeLabel = (theme: { name: string; isCustom: boolean; displayName?: string }) => {
    if (theme.displayName) return theme.displayName;
    if (!theme.isCustom) return t(`theme:${theme.name}`);
    return theme.name;
  };

  return (
    <div style={SETTINGS_CARD}>
      <div style={SETTINGS_LABEL}>{t('theme:theme')}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={SETTINGS_SELECT}>
        {availableThemes.map((th) => (
          <option key={th.name} value={th.name}>
            {themeIcon(th)}  {themeLabel(th)}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ================================================================== */
/*  Language Switcher — dropdown with flags                             */
/* ================================================================== */

/** Supported languages with flag emoji and default calendar system. */
const SUPPORTED_LANGUAGES = [
  { code: 'en', flag: '\uD83C\uDDFA\uD83C\uDDF8', defaultCalendar: 'gregorian' as const },
  { code: 'ar', flag: '\uD83C\uDDF8\uD83C\uDDE6', defaultCalendar: 'hijri' as const },
  { code: 'fr', flag: '\uD83C\uDDEB\uD83C\uDDF7', defaultCalendar: 'gregorian' as const },
  { code: 'de', flag: '\uD83C\uDDE9\uD83C\uDDEA', defaultCalendar: 'gregorian' as const },
  { code: 'uk', flag: '\uD83C\uDDFA\uD83C\uDDE6', defaultCalendar: 'gregorian' as const },
  { code: 'sv', flag: '\uD83C\uDDF8\uD83C\uDDEA', defaultCalendar: 'gregorian' as const },
  { code: 'ru', flag: '\uD83C\uDDF7\uD83C\uDDFA', defaultCalendar: 'gregorian' as const },
  { code: 'es', flag: '\uD83C\uDDEA\uD83C\uDDF8', defaultCalendar: 'gregorian' as const },
] as const;

function LanguageSwitcher({ value, onChange, onCalendarHint }: {
  value: string;
  onChange: (code: string) => void;
  onCalendarHint: (cal: string) => void;
}) {
  const { t } = useTranslation();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    onChange(code);
    // Auto-hint the calendar default for the selected language
    const langDef = SUPPORTED_LANGUAGES.find((l) => l.code === code);
    if (langDef) onCalendarHint(langDef.defaultCalendar);
  }, [onChange, onCalendarHint]);

  return (
    <div style={SETTINGS_CARD}>
      <div style={SETTINGS_LABEL}>{t('theme:settings.language')}</div>
      <select value={value} onChange={handleChange} style={SETTINGS_SELECT}>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag}  {t(`theme:lang.${lang.code}`)}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ================================================================== */
/*  Calendar Preference — Gregorian / Hijri dropdown                    */
/* ================================================================== */

function CalendarPreference({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();

  return (
    <div style={SETTINGS_CARD}>
      <div style={SETTINGS_LABEL}>{t('theme:settings.calendar')}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={SETTINGS_SELECT}>
        <option value="gregorian">{'\uD83D\uDCC5'}  {t('theme:calendar.gregorian')}</option>
        <option value="hijri">{'\uD83D\uDD4C'}  {t('theme:calendar.hijri')}</option>
      </select>
    </div>
  );
}

/* §7.9.10 cleanup (2026-07-13): the dead duplicate DefaultCommunityViewSwitcher
   (localStorage-only, never rendered) was DELETED. The live Start Page control
   is StartPageSwitcher in AccountSettingsModal.tsx. */

/* ================================================================== */
/*  Toggle Switch — Inline sub-component                                */
/* ================================================================== */

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(!checked); } }}
      style={{
        width: 36, height: 20, borderRadius: 10,
        background: checked ? 'var(--accent-primary)' : 'var(--bg-active)',
        cursor: 'pointer', position: 'relative',
        transition: 'background 150ms ease', flexShrink: 0,
        border: checked ? 'none' : '1px solid var(--border-default)',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: '50%',
        background: '#fff',
        position: 'absolute', top: checked ? 2 : 1, left: checked ? 18 : 2,
        transition: 'left 150ms ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

/* ================================================================== */
/*  About Tab — Inline sub-component                                   */
/* ================================================================== */

function AboutTabContent({
  user, isOwnProfile,
  formName, setFormName,
  formDisplayName, setFormDisplayName,
  formEmail, setFormEmail,
  formNotes, setFormNotes,
  formPrimaryLocation, setFormPrimaryLocation,
  formScreenName, setFormScreenName,
  screenNameStatus,
  getCommunityName,
  locationQuery, setLocationQuery,
  showLocationSuggestions, setShowLocationSuggestions,
  locationSuggestions, selectedLocationLabel,
  formOperationalLocation, setFormOperationalLocation,
  opLocationQuery, setOpLocationQuery,
  showOpLocationSuggestions, setShowOpLocationSuggestions,
  opLocationSuggestions, selectedOpLocationLabel,
  formShowHome, setFormShowHome,
  formShowOp, setFormShowOp,
  formIsSearchable, setFormIsSearchable,
}: {
  user: SecurityUserResponse | null;
  isOwnProfile: boolean;
  formName: string; setFormName: (v: string) => void;
  formDisplayName: string; setFormDisplayName: (v: string) => void;
  formEmail: string; setFormEmail: (v: string) => void;
  formNotes: string; setFormNotes: (v: string) => void;
  formPrimaryLocation: string; setFormPrimaryLocation: (v: string) => void;
  formScreenName: string; setFormScreenName: (v: string) => void;
  screenNameStatus: 'idle' | 'checking' | 'available' | 'taken';
  locationQuery: string; setLocationQuery: (v: string) => void;
  showLocationSuggestions: boolean; setShowLocationSuggestions: (v: boolean) => void;
  locationSuggestions: { guid: string; display: string }[];
  selectedLocationLabel: string;
  formOperationalLocation: string; setFormOperationalLocation: (v: string) => void;
  opLocationQuery: string; setOpLocationQuery: (v: string) => void;
  showOpLocationSuggestions: boolean; setShowOpLocationSuggestions: (v: boolean) => void;
  opLocationSuggestions: { guid: string; display: string }[];
  selectedOpLocationLabel: string;
  formShowHome: boolean; setFormShowHome: (v: boolean) => void;
  formShowOp: boolean; setFormShowOp: (v: boolean) => void;
  formIsSearchable: boolean; setFormIsSearchable: (v: boolean) => void;
  getCommunityName: (guid: string) => string;
}) {
  const { t } = useTranslation();
  if (!user) return null;

  return (
    <div style={{ paddingTop: 16 }}>
      {/* Profile Fields */}
      <div style={ST.section}>
        <div style={ST.sectionTitle}>Profile Information</div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Full Name</label>
          <input style={ST.fieldInput} value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Legal or full name" disabled={!isOwnProfile} />
        </div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Display Name</label>
          <input style={ST.fieldInput} value={formDisplayName}
            onChange={(e) => setFormDisplayName(e.target.value)}
            placeholder="How you appear to others" disabled={!isOwnProfile} />
        </div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Handle</label>
          <div style={{ flex: 1 }}>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--accent-primary)', fontWeight: 600, fontSize: 14,
              }}>@</span>
              <input
                style={{ ...ST.fieldInput, paddingInlineStart: 28 }}
                value={formScreenName}
                onChange={(e) => setFormScreenName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
                placeholder="yourhandle"
                disabled={!isOwnProfile}
                maxLength={30}
              />
            </div>
            {screenNameStatus === 'checking' && (
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4, display: 'block' }}>Checking availability...</span>
            )}
            {screenNameStatus === 'available' && (
              <span style={{ fontSize: 12, color: '#45BB47', marginTop: 4, display: 'block' }}>Available</span>
            )}
            {screenNameStatus === 'taken' && (
              <span style={{ fontSize: 12, color: '#D53541', marginTop: 4, display: 'block' }}>This handle is already taken</span>
            )}
          </div>
        </div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Email</label>
          <input style={ST.fieldInput} type="email" value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="your@email.com" disabled={!isOwnProfile} />
        </div>

        {/* Home Community (nationality) */}
        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Home Community</label>
          <div style={{ flex: 1 }}>
            <div style={{ position: 'relative' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                background: 'var(--bg-hover)', border: '1px solid var(--border-default)',
                borderRadius: 8, padding: '4px 8px', minHeight: 38,
              }}>
                {formPrimaryLocation && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--accent-bg)', border: '1px solid var(--accent-border)',
                    borderRadius: 6, padding: '3px 8px', fontSize: 13, color: 'var(--accent-text)',
                    maxWidth: '100%', overflow: 'hidden',
                  }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedLocationLabel}
                    </span>
                    {isOwnProfile && (
                      <button type="button" onClick={() => {
                        setFormPrimaryLocation(''); setLocationQuery(''); setShowLocationSuggestions(false);
                      }} style={{
                        background: 'none', border: 'none', color: 'var(--accent-text)', cursor: 'pointer',
                        fontSize: 16, lineHeight: 1, padding: '0 2px', flexShrink: 0,
                      }} aria-label="Clear home community">&times;</button>
                    )}
                  </div>
                )}
                <input
                  style={{
                    flex: 1, minWidth: 120, background: 'transparent', border: 'none',
                    color: 'var(--text-primary)', fontSize: 14, padding: '4px 0', outline: 'none',
                  }}
                  value={locationQuery}
                  placeholder={formPrimaryLocation ? 'Type to change...' : 'Type a city, state, or country...'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (formPrimaryLocation && v.trim().length > 0) setFormPrimaryLocation('');
                    setLocationQuery(v);
                    setShowLocationSuggestions(true);
                  }}
                  onFocus={() => setShowLocationSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                  disabled={!isOwnProfile}
                />
              </div>
              {showLocationSuggestions && locationSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', insetInline: 0, zIndex: 50,
                  background: 'var(--bg-tertiary)', border: '1px solid var(--border-default)', borderRadius: 8,
                  marginTop: 4, maxHeight: 220, overflowY: 'auto',
                  boxShadow: 'var(--shadow-lg)',
                }}>
                  {locationSuggestions.map((s) => (
                    <div key={s.guid}
                      onMouseDown={() => {
                        setFormPrimaryLocation(s.guid);
                        setLocationQuery('');
                        setShowLocationSuggestions(false);
                      }}
                      style={{
                        padding: '8px 12px', fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
                        borderBottom: '1px solid var(--border-subtle)',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--accent-bg)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                    >
                      {s.display}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Privacy toggle */}
            {isOwnProfile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}
                onClick={() => setFormShowHome(!formShowHome)}>
                <ToggleSwitch checked={formShowHome} onChange={setFormShowHome} />
                Share on profile
              </div>
            )}
          </div>
        </div>

        {/* Operational Community */}
        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Operational Community</label>
          <div style={{ flex: 1 }}>
            <div style={{ position: 'relative' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                background: 'var(--bg-hover)', border: '1px solid var(--border-default)',
                borderRadius: 8, padding: '4px 8px', minHeight: 38,
              }}>
                {formOperationalLocation && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--accent-bg)', border: '1px solid var(--accent-border)',
                    borderRadius: 6, padding: '3px 8px', fontSize: 13, color: 'var(--accent-text)',
                    maxWidth: '100%', overflow: 'hidden',
                  }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedOpLocationLabel}
                    </span>
                    {isOwnProfile && (
                      <button type="button" onClick={() => {
                        setFormOperationalLocation(''); setOpLocationQuery(''); setShowOpLocationSuggestions(false);
                      }} style={{
                        background: 'none', border: 'none', color: 'var(--accent-text)', cursor: 'pointer',
                        fontSize: 16, lineHeight: 1, padding: '0 2px', flexShrink: 0,
                      }} aria-label="Clear operational community">&times;</button>
                    )}
                  </div>
                )}
                <input
                  style={{
                    flex: 1, minWidth: 120, background: 'transparent', border: 'none',
                    color: 'var(--text-primary)', fontSize: 14, padding: '4px 0', outline: 'none',
                  }}
                  value={opLocationQuery}
                  placeholder={formOperationalLocation ? 'Type to change...' : 'Where do you currently operate?'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (formOperationalLocation && v.trim().length > 0) setFormOperationalLocation('');
                    setOpLocationQuery(v);
                    setShowOpLocationSuggestions(true);
                  }}
                  onFocus={() => setShowOpLocationSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowOpLocationSuggestions(false), 200)}
                  disabled={!isOwnProfile}
                />
              </div>
              {showOpLocationSuggestions && opLocationSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', insetInline: 0, zIndex: 50,
                  background: 'var(--bg-tertiary)', border: '1px solid var(--border-default)', borderRadius: 8,
                  marginTop: 4, maxHeight: 220, overflowY: 'auto',
                  boxShadow: 'var(--shadow-lg)',
                }}>
                  {opLocationSuggestions.map((s) => (
                    <div key={s.guid}
                      onMouseDown={() => {
                        setFormOperationalLocation(s.guid);
                        setOpLocationQuery('');
                        setShowOpLocationSuggestions(false);
                      }}
                      style={{
                        padding: '8px 12px', fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
                        borderBottom: '1px solid var(--border-subtle)',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--accent-bg)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                    >
                      {s.display}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Privacy toggle */}
            {isOwnProfile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}
                onClick={() => setFormShowOp(!formShowOp)}>
                <ToggleSwitch checked={formShowOp} onChange={setFormShowOp} />
                Share on profile
              </div>
            )}
          </div>
        </div>

        {/* Bio field removed (Skip, 2026-07-07) — it bound formNotes but had
            NO save path (edits were silently discarded). The bio is owned by
            the About tab's inline "Short bio" editor now. */}
      </div>

      {/* Search visibility */}
      {isOwnProfile && (
        <div style={ST.section}>
          <div style={ST.sectionTitle}>{t('userProfile:searchVisibility.title')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '4px 0' }}>
            <ToggleSwitch checked={formIsSearchable} onChange={setFormIsSearchable} />
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                {t('userProfile:searchVisibility.label')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {t('userProfile:searchVisibility.description')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System info */}
      <div style={{ ...ST.section, background: 'var(--bg-primary)' }}>
        <div style={ST.sectionTitle}>System Information</div>
        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>User GUID</label>
          <input style={{ ...ST.fieldInput, color: 'var(--text-muted)', cursor: 'text' }}
            value={user.userGuid} readOnly />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles                                                              */
/* ------------------------------------------------------------------ */

/* ── Hero button styles (matches ProjectDetailPage pattern) ── */
const heroBtnStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.65)',
  backdropFilter: 'blur(6px)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  color: 'var(--text-secondary)',
  fontSize: 12,
  fontWeight: 600,
  padding: '5px 12px',
  cursor: 'pointer',
  transition: 'background 0.15s',
};

const heroMenuItemStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left',
  background: 'transparent', border: 'none',
  borderBottom: '1px solid var(--border-subtle)',
  color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 500,
  padding: '10px 16px', cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s',
};

const heroRemoveBtnStyle: React.CSSProperties = {
  background: 'rgba(213,53,65,0.70)',
  backdropFilter: 'blur(6px)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  padding: '5px 12px',
  cursor: 'pointer',
  transition: 'background 0.15s',
};

const ST = {
  section: {
    background: 'var(--bg-secondary)', borderRadius: 8, padding: 24, marginBottom: 20,
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16,
    letterSpacing: '0.03em', textTransform: 'uppercase' as const,
  } as React.CSSProperties,
  fieldRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16,
  } as React.CSSProperties,
  fieldLabel: {
    width: 140, flexShrink: 0, fontSize: 13, color: 'var(--text-tertiary)', paddingTop: 8, textAlign: 'right' as const,
  } as React.CSSProperties,
  fieldInput: {
    flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', borderRadius: 6,
    padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', outline: 'none',
  } as React.CSSProperties,
  fieldSelect: {
    flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', borderRadius: 6,
    padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', outline: 'none', cursor: 'pointer',
  } as React.CSSProperties,
  fieldTextarea: {
    flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', borderRadius: 6,
    padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', outline: 'none',
    minHeight: 80, resize: 'vertical' as const, fontFamily: 'inherit',
  } as React.CSSProperties,
  saveRow: {
    display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4,
  } as React.CSSProperties,
  btnPrimary: {
    background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6,
    padding: '8px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  } as React.CSSProperties,
  btnSecondary: {
    background: 'var(--bg-active)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6,
    padding: '8px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
  } as React.CSSProperties,
  statusMsg: {
    fontSize: 13, padding: '6px 0', textAlign: 'right' as const,
  } as React.CSSProperties,
  badge: {
    background: 'var(--bg-active)', borderRadius: 4, padding: '2px 8px',
    fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const,
    fontWeight: 600, letterSpacing: '0.04em',
  } as React.CSSProperties,
  loading: {
    textAlign: 'center' as const, padding: 48, color: 'var(--text-tertiary)', fontSize: 15,
  } as React.CSSProperties,
  error: {
    textAlign: 'center' as const, padding: 48, color: 'var(--color-error)', fontSize: 15,
  } as React.CSSProperties,
  tabPlaceholder: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center',
    padding: '64px 32px', textAlign: 'center' as const,
  } as React.CSSProperties,
  placeholderIcon: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'var(--accent-bg)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', marginBottom: 20,
  } as React.CSSProperties,
  placeholderTitle: {
    margin: '0 0 8px', fontSize: 20, fontWeight: 600, color: 'var(--text-primary)',
  } as React.CSSProperties,
  placeholderText: {
    margin: '0 0 20px', fontSize: 14, color: 'var(--text-tertiary)', maxWidth: 440, lineHeight: 1.6,
  } as React.CSSProperties,
  comingSoon: {
    display: 'inline-block', background: 'var(--bg-hover)',
    border: '1px solid var(--border-subtle)', borderRadius: 20,
    padding: '6px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
    letterSpacing: '0.06em', textTransform: 'uppercase' as const,
  } as React.CSSProperties,
};
